import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import { ASSESSMENT_TYPE_DESCRIPTIONS } from '../../utils/hiraConstants';
import AIPrefillPanel from '../../components/assistant/AIPrefillPanel';
import SimilarRecordsPanel from '../../components/assistant/SimilarRecordsPanel';
import AIDraftWarningBanner, { AIVerifyAcknowledgement } from '../../components/assistant/AIDraftWarningBanner';
import useAIDraftForm from '../../hooks/useAIDraftForm';
import useSimilarRecords from '../../hooks/useSimilarRecords';
import * as hiraDraftMapper from '../../utils/aiPrefill/hiraDraftMapper';
import { todayTenantISO, getTenantTimezone } from '../../utils/dateUtils';
import { nowInTenantInput } from '../../utils/tenantTime';

const HIRACreate = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showError } = useAlert();
  const { activeSiteId, activeSiteName } = useSite();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [previousAssessments, setPreviousAssessments] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assessment_type: 'Activity-based',
    department_id: '',
    location: '',
    review_date: '',
    site_id: '',
    site_name: '',
    // MOM Fields
    meeting_datetime: '',
    meeting_location: '',
    facilitator_id: '',
    facilitator_name: '',
    mom_attendees: [],
    previous_assessment_id: ''
  });

  // Track whether the site has been set for the first time
  const siteInitialized = useRef(false);

  // AI prefill: shared draft state (mandatory warning banner, fill-empty-only
  // merge, chat handoff consumption, feedback beacon).
  const ai = useAIDraftForm({
    module: 'hira',
    mapper: hiraDraftMapper,
    formData,
    setFormData,
    getContext: () => ({ departments }),
  });
  const { suggestions: similarAssessments, loading: similarLoading } = useSimilarRecords(
    'hira', `${formData.title} ${formData.description}`.trim()
  );

  // Auto-set site from active site context — only update site fields, never reset other form data
  useEffect(() => {
    if (activeSiteId) {
      setFormData(prev => ({ ...prev, site_id: activeSiteId, site_name: activeSiteName || '' }));
      siteInitialized.current = true;
    } else {
      setFormData(prev => ({ ...prev, site_id: '', site_name: '' }));
    }
  }, [activeSiteId, activeSiteName]);

  useEffect(() => {
    fetchUsers();
    fetchPreviousAssessments();
  }, [activeSiteId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/departments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDepartments(res.data.departments || []))
      .catch(() => setDepartments([]));
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const response = await axios.get(
        `${API}/users${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchPreviousAssessments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/risk-assessments?status=Active`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // List endpoint returns a plain array
      const allAssessments = Array.isArray(response.data) ? response.data : (response.data.risk_assessments || []);
      setPreviousAssessments(allAssessments);
    } catch (error) {
      console.error('Error fetching previous assessments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.site_id) {
      showError('Please select a site from the header before creating the assessment.');
      return;
    }
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/risk-assessments`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Evaluation beacon (field names only; failures swallowed).
      ai.sendFeedback(formData);

      // Navigate to detail page to add hazards
      navigate(`/hira/${response.data.id}`);
    } catch (err) {
      console.error('Failed to create assessment:', err);
      showError('Failed to create risk assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFacilitatorChange = (e) => {
    const userId = e.target.value;
    const selectedUser = users.find(u => u.id === userId);
    setFormData(prev => ({
      ...prev,
      facilitator_id: userId,
      facilitator_name: selectedUser ? selectedUser.name : ''
    }));
  };

  const handleAttendeesChange = (e) => {
    const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
    const selectedAttendees = selectedIds.map(id => {
      const u = users.find(user => user.id === id);
      return {
        user_id: id,
        user_name: u?.name || '',
        department: u?.department || '',
        role: u?.role || ''
      };
    });
    setFormData(prev => ({ ...prev, mom_attendees: selectedAttendees }));
  };

  const removeAttendee = (userId) => {
    setFormData(prev => ({
      ...prev,
      mom_attendees: prev.mom_attendees.filter(a => a.user_id !== userId)
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/hira')}
            className="text-[#1B5C94] hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Risk Assessments
          </button>
          <h1 className="text-3xl font-bold text-gray-800">Create New Risk Assessment</h1>
          <p className="text-gray-600 mt-1">Fill in the basic details. You'll add hazards in the next step.</p>
        </div>

        {/* BUG-283: If no site is selected yet, gate the form entirely so a
            user cannot fill fields that would then be lost on submit. Show a
            full-page prompt instead of rendering the inputs below. */}
        {!activeSiteId ? (
          <div className="bg-white rounded-xl shadow-md p-10 text-center border border-amber-300">
            <svg className="w-12 h-12 text-amber-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Select a site to continue</h2>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
              Risk assessments are site-scoped. Use the site selector in the header
              to choose the site this assessment applies to. The form will load once
              a site is active.
            </p>
          </div>
        ) : (
        /* Form */
        <div className="space-y-4">
          {/* MANDATORY AI warning — shown every time data is pre-populated */}
          <AIDraftWarningBanner
            aiFieldCount={ai.aiFields.size}
            lowCertaintyFields={ai.lowCertaintyFields}
            appliedAt={ai.aiMeta?.appliedAt}
          />

          <AIPrefillPanel
            module="hira"
            onDraft={ai.applyAIDraft}
            placeholder="Describe the activity to assess, e.g. 'Routine maintenance welding in the main workshop by two fitters'"
          />

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Assessment Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g., Welding Operations - Main Workshop"
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent${ai.aiClass('title')}`}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Provide additional details about this risk assessment..."
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent${ai.aiClass('description')}`}
            />
            <SimilarRecordsPanel
              suggestions={similarAssessments}
              loading={similarLoading}
              title="similar past assessments"
              onStartFrom={(rec) =>
                ai.applyAIDraft({ draft: hiraDraftMapper.draftFromSimilarRecord(rec) })}
            />
          </div>

          {/* Assessment Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Assessment Type <span className="text-red-500">*</span>
            </label>
            <select
              name="assessment_type"
              value={formData.assessment_type}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
            >
              <option value="Activity-based">Activity-based</option>
              <option value="Task-based">Task-based</option>
              <option value="JSA">Job Safety Analysis (JSA)</option>
              <option value="General">General Assessment</option>
            </select>
            {/* Description text */}
            {formData.assessment_type && (
              <p className="text-xs text-gray-600 mt-2">
                {ASSESSMENT_TYPE_DESCRIPTIONS[formData.assessment_type]}
              </p>
            )}
          </div>

          {/* Site (auto-set from header site switcher) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Site <span className="text-red-500">*</span>
            </label>
            {activeSiteId ? (
              <div className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                {activeSiteName || 'Loading...'}
              </div>
            ) : (
              <div className="w-full px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
                Please select a site from the header first.
              </div>
            )}
          </div>

          {/* Department & Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Department
              </label>
              <select
                name="department_id"
                value={formData.department_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              >
                <option value="">— Select department —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., Workshop A, Site 2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              />
            </div>
          </div>

          {/* Review Date — BUG-285: renamed from "Next Review Date" per QA */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Review Date
            </label>
            <input
              type="date"
              name="review_date"
              value={formData.review_date}
              onChange={handleChange}
              /* REG-10: "today" in the tenant's timezone, not UTC */
              min={todayTenantISO()}
              max="2099-12-31"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: Review annually or when conditions change</p>
          </div>

          {/* MOM Section */}
          <div className="border-t pt-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Minutes of Meeting (MOM) Details
            </h2>

            {/* Meeting Date/Time and Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Meeting Date & Time
                </label>
                <input
                  type="datetime-local"
                  name="meeting_datetime"
                  value={formData.meeting_datetime}
                  onChange={handleChange}
                  /* BUG-284: Disallow past values client-side. The backend now
                     also validates this; min is derived now() to the nearest
                     minute in the local datetime-local format.
                     REG-10: aligned to the tenant's wall clock (same zone as
                     the Review Date min above) instead of the browser's. */
                  min={nowInTenantInput(getTenantTimezone())}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Meeting Location
                </label>
                <input
                  type="text"
                  name="meeting_location"
                  value={formData.meeting_location}
                  onChange={handleChange}
                  placeholder="e.g., Conference Room A"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                />
              </div>
            </div>

            {/* Facilitator */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Facilitator
              </label>
              <select
                value={formData.facilitator_id}
                onChange={handleFacilitatorChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              >
                <option value="">Select Facilitator</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role || 'No Role'})
                  </option>
                ))}
              </select>
            </div>

            {/* Attendees */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Attendees (Hold Ctrl/Cmd to select multiple)
              </label>
              <select
                multiple
                value={formData.mom_attendees.map(a => a.user_id)}
                onChange={handleAttendeesChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent h-32"
              >
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name} - {u.department || 'No Dept'} ({u.role || 'No Role'})
                  </option>
                ))}
              </select>
              {formData.mom_attendees.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.mom_attendees.map(att => (
                    <span
                      key={att.user_id}
                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm flex items-center gap-1"
                    >
                      {att.user_name}
                      <button
                        type="button"
                        onClick={() => removeAttendee(att.user_id)}
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Previous Assessment Reference — hidden on new assessment create form (BUG-030) */}
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border-l-4 border-[#1B5C94] p-4 rounded">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-[#1B5C94] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">Next Steps:</p>
                <p>After creating this assessment, you'll be able to add hazards, assess risks using the 5×5 matrix, 
                and implement control measures following the hierarchy of controls.</p>
              </div>
            </div>
          </div>

          {/* Mandatory AI verification acknowledgement — blocks submit until
              ticked whenever AI populated any field this session. */}
          {ai.requiresAck && (
            <AIVerifyAcknowledgement
              checked={ai.aiVerifiedAck}
              onChange={ai.setAiVerifiedAck}
              unreviewedCount={ai.aiFields.size}
            />
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/hira')}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.title || !formData.site_id || (ai.requiresAck && !ai.aiVerifiedAck)}
              title={ai.requiresAck && !ai.aiVerifiedAck ? 'Confirm you have verified all AI-generated fields first' : undefined}
              className="flex-1 px-6 py-3 bg-[#1B5C94] text-white font-semibold rounded-lg hover:bg-[#164A7B] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Assessment'}
            </button>
          </div>
        </form>
        </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default HIRACreate;
